/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'utils/toastUtils'
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'

import { Reminder } from 'types/entities/Reminder'
import { reminderSchema } from 'components/Reminders/schemas/reminderSchema'
import { RootState, useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import HallAction from 'store/hall/HallAction'
import SettingsAction from 'store/settings/SettingsAction'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiFormSelectField from 'components/Forms/UiFormSelectField'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiAlert from 'components/Ui/UiAlert'
import UiButton from 'components/Ui/UiButton'
import UiFormIntervalPicker from 'components/Forms/UiFormIntervalPicker'
import { UiSelectOption } from 'components/Ui/UiSelectWithGraphics'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiTimePicker from 'components/Ui/UiTimePicker'
import { useCurrentUser } from 'utils/hooks/useCurrentUser'
import { useSellers } from 'utils/sellerUtils'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { normalizeReminderModelName } from 'utils/reminders/reminderUtils'
import {changeReminderDate, changeReminderTime} from 'utils/reminders/reminderDateFields'

interface ReminderFormProps {
    reminder?: Partial<Reminder> & { model_id?: number }
    onSubmit: (data: any, shouldClose?: boolean) => Promise<void>
    modelContext?: { id: number; name: string }
}

const ReminderForm: React.FC<ReminderFormProps> = ({ reminder, onSubmit, modelContext }) => {
    const { t } = useTranslation()
    const settingsLoadErrorMessage = t('Unable to load settings. Please try again.')
    const dispatch = useAppDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hallsLoadFailed, setHallsLoadFailed] = useState(false)
    const [defaultDueAt] = useState(() => new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())

    const hallsList = useTypedSelector((state: RootState) => state.hallState.hallsList)
    const sellers = useTypedSelector((state: RootState) => state.settings.sellers)
    const { user } = useCurrentUser()
    const { selectOptions } = useSellers()

    const defaultModelName =
        normalizeReminderModelName(modelContext?.name ?? reminder?.model_name ?? null) ??
        ReminderModelName.Company

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(reminderSchema),
        defaultValues: {
            model_id: modelContext?.id ?? reminder?.model_id ?? reminder?.model?.id ?? null,
            model_name: defaultModelName,
            type: reminder?.type || 'follow_up',
            hall_id: reminder?.hall_id || (hallsList?.[0]?.id ?? 1),
            seller_id: reminder?.seller_id || user?.id || null,
            interval: reminder?.interval ?? 1440,
            content: reminder?.content || '',
            due_at: reminder?.due_at || defaultDueAt,
            expires_at: reminder?.expires_at || null,
        },
    })

    const getModelNameOptions = (): UiSelectOption[] => [
        { label: t('Company'), value: ReminderModelName.Company },
        { label: t('Contact'), value: ReminderModelName.Contact },
        { label: t('Lead'), value: ReminderModelName.Lead },
        { label: t('Offer'), value: ReminderModelName.Offer },
        { label: t('Invoice'), value: ReminderModelName.Invoice },
        { label: t('Driver'), value: ReminderModelName.Driver },
        { label: t('Seller'), value: ReminderModelName.Seller },
    ]

    const getTypeOptions = (): UiSelectOption[] => [
        { label: t('Follow Up'), value: 'follow_up' },
        { label: t('Call'), value: 'call' },
        { label: t('Email'), value: 'email' },
        { label: t('Meeting'), value: 'meeting' },
    ]

    // callbacks
    const handleDateChange = (date: Date | null, value: string, onChange: (val: string) => void) => {
        if (!date) return
        onChange(changeReminderDate(value, date))
    }

    const handleTimeChange = (date: Date | null, value: string, onChange: (val: string) => void) => {
        if (!date) return
        onChange(changeReminderTime(value, date))
    }

    const handleFormSubmit = async (data: any, shouldClose: boolean = true) => {
        if (modelContext && !normalizeReminderModelName(modelContext.name)) {
            toast.error(t('Please fix validation errors'))
            return
        }
        setIsSubmitting(true)
        try {
            const payload = {
                ...data,
                seller_id: data.seller_id === '' ? null : data.seller_id,
            }
            await onSubmit(payload, shouldClose)
        } catch {
            toast.error(t('An error occurred while saving the reminder'))
        } finally {
            setIsSubmitting(false)
        }
    }

    // useEffects
    useEffect(() => {
        if (!hallsList) {
            void dispatch(HallAction.fetchHalls()).then(
                () => setHallsLoadFailed(false),
                () => setHallsLoadFailed(true),
            )
        }
        if (!sellers) {
            void dispatch(SettingsAction.fetchSettings() as any).catch(() => toast.error(settingsLoadErrorMessage))
        }
    }, [dispatch, hallsList, sellers, settingsLoadErrorMessage])

    return (
        <form onSubmit={handleSubmit((data) => handleFormSubmit(data, true))} className="ReminderForm flex flex-col">
            {hallsLoadFailed && <div role="alert" className="mb-4">
                <UiAlert variant="error" title={t('Failed to load venues')} description={t('Please try again.')} />
                <div className="mt-2"><UiButton title={t('Retry')} onClick={() => {
                    setHallsLoadFailed(false)
                    void dispatch(HallAction.fetchHalls()).catch(() => setHallsLoadFailed(true))
                }} /></div>
            </div>}
            <UiFormInner title={t('Reminder Details')}>
                <UiFormGrid cols={12} gap={6}>
                    {!modelContext && (
                        <Controller
                            control={control}
                            name="model_name"
                            render={({ field, fieldState }) => (
                                <UiFormSelectField
                                    className="col-span-4"
                                    label={t('Related To')}
                                    isRequired
                                    options={getModelNameOptions()}
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                    )}

                    <Controller
                        control={control}
                        name="type"
                        render={({ field, fieldState }) => (
                            <UiFormSelectField
                                className="col-span-6"
                                label={t('Type')}
                                isRequired
                                options={getTypeOptions()}
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                error={fieldState.error?.message}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="seller_id"
                        render={({ field, fieldState }) => (
                            <UiFormSelectField
                                className="col-span-6"
                                label={t('Seller')}
                                isRequired
                                options={selectOptions}
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={(val) => field.onChange(val === '' ? null : Number(val))}
                                error={fieldState.error?.message}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="due_at"
                        render={({ field }) => (
                            <UiFormField
                                className="col-span-4 z-50"
                                label={t('Due Date')}
                                required
                                error={errors.due_at?.message}
                            >
                                <UiDatePicker
                                    required
                                    value={field.value ? new Date(field.value) : undefined}
                                    includeTime={false}
                                    onChange={(date) => handleDateChange(date, field.value, field.onChange)}
                                    endIcon={<CalendarIcon className="w-4 h-4" />}
                                />
                            </UiFormField>
                        )}
                    />

                    <Controller
                        control={control}
                        name="due_at"
                        render={({ field }) => (
                            <UiFormField required className="col-span-4" label={t('Due Time')} error={errors.due_at?.message}>
                                <UiTimePicker
                                    required
                                    value={field.value ? new Date(field.value) : null}
                                    onChange={(date) => handleTimeChange(date, field.value, field.onChange)}
                                    endIcon={<ClockIcon className="w-4 h-4" />}
                                />
                            </UiFormField>
                        )}
                    />

                    <Controller
                        control={control}
                        name="interval"
                        render={({ field, fieldState }) => (
                            <UiFormField label={t('Interval')} className="col-span-4" error={fieldState.error?.message}>
                                <UiFormIntervalPicker value={field.value ?? null} onChange={field.onChange} />
                            </UiFormField>
                        )}
                    />

                    <CompanyFormInput
                        className="col-span-12"
                        label={t('Content')}
                        required
                        name="content"
                        component="textarea"
                        register={register}
                        errors={errors}
                        placeholder={t('Reminder content...')}
                    />
                </UiFormGrid>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Saving...') : t('Save')}
                onPrimary={handleSubmit((data) => handleFormSubmit(data, false))}
                saveAndCloseText={isSubmitting ? t('Saving...') : t('Save & Close')}
                onSaveAndClose={handleSubmit((data) => handleFormSubmit(data, true))}
                loading={isSubmitting}
            />
        </form>
    )
}

export default ReminderForm
